import React, { useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough,
  ListBullets, ListNumbers, Quotes, TextAlignLeft, TextAlignCenter,
  TextAlignRight, ArrowUUpLeft, ArrowUUpRight, Code, Eye, EyeSlash,
  PlusCircle,
} from '@phosphor-icons/react';
import DialogModal from '../DialogModal';
import './CertificateContentEditor.css';

// Sample values used only to render an approximate client-side preview.
// The real PDF is generated server-side with full Jinja2 rendering.
const SAMPLE_DATA = {
  '{{ school_name }}': 'Greenwood International School',
  '{{ student_name }}': 'Jordan A. Smith',
  '{{ student_id_code }}': 'GW-2024-0123',
  '{{ date_of_birth }}': '14 March 2010',
  '{{ last_class_completed }}': 'Grade 9',
  '{{ certificate_number }}': 'CERT-000123',
  '{{ issue_date }}': '18 August 2026',
  '{{ remarks }}': 'Conduct and character: Excellent.',
  '{{ generated_date }}': '18 August 2026',
};

// A template is "advanced" (full HTML document / Jinja2 control flow) when it
// carries markup a rich-text schema can't round-trip safely — <head>/<style>
// blocks or {% if %}/{% for %} tags. Those must stay in source mode so we
// never silently strip content a WYSIWYG parser doesn't understand.
function looksAdvanced(html) {
  return /<html[\s>]|<head[\s>]|<style[\s>]|\{%-?\s*(if|for)\b/i.test(html || '');
}

function buildPreviewHtml(html) {
  let out = html || '';
  Object.entries(SAMPLE_DATA).forEach(([token, value]) => {
    out = out.split(token).join(value);
  });
  // Approximate Jinja2 control tags for preview purposes only: keep the
  // inner content, drop the tag itself, since sample data is always "present".
  out = out.replace(/\{%-?\s*(if|for)[^%]*%\}/gi, '');
  out = out.replace(/\{%-?\s*(endif|endfor|else)[^%]*%\}/gi, '');
  return out;
}

function ToolbarButton({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      className={`cce-toolbar-btn${active ? ' active' : ''}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
    >
      {children}
    </button>
  );
}

function CertificateContentEditor({ value, onChange, placeholders = [], disabled = false }) {
  const [mode, setMode] = useState(() => (looksAdvanced(value) ? 'source' : 'visual'));
  const [showPreview, setShowPreview] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, type, confirmLabel, onConfirm }
  const textareaRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Write the certificate wording here…' }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  const previewHtml = useMemo(() => buildPreviewHtml(value), [value]);

  const insertPlaceholder = (token) => {
    if (mode === 'visual' && editor) {
      editor.chain().focus().insertContent(token).run();
      return;
    }
    const el = textareaRef.current;
    if (!el) {
      onChange((value || '') + token);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + token.length;
    });
  };

  const switchToVisual = () => {
    if (looksAdvanced(value)) {
      setConfirmDialog({
        title: 'Switch to Visual Editor',
        message: 'This template uses a full HTML document (or {% if %}/{% for %} logic) that ' +
          'the visual editor cannot fully represent. Switching may simplify or remove ' +
          'that markup. Continue?',
        type: 'warning',
        confirmLabel: 'Continue',
        onConfirm: () => {
          editor?.commands.setContent(value || '');
          setMode('visual');
        },
      });
      return;
    }
    editor?.commands.setContent(value || '');
    setMode('visual');
  };

  const switchToSource = () => {
    setMode('source');
  };

  return (
    <div className="cce-wrapper">
      <div className="cce-header">
        <div className="cce-mode-tabs">
          <button
            type="button"
            className={`cce-mode-tab${mode === 'visual' ? ' active' : ''}`}
            onClick={switchToVisual}
            disabled={disabled}
          >
            Visual Editor
          </button>
          <button
            type="button"
            className={`cce-mode-tab${mode === 'source' ? ' active' : ''}`}
            onClick={switchToSource}
            disabled={disabled}
          >
            <Code size={14} weight="bold" /> HTML Source
          </button>
        </div>
        <button
          type="button"
          className="cce-preview-toggle"
          onClick={() => setShowPreview((v) => !v)}
        >
          {showPreview ? <EyeSlash size={16} /> : <Eye size={16} />}
          {showPreview ? 'Hide Preview' : 'Preview (sample data)'}
        </button>
      </div>

      {placeholders.length > 0 && (
        <div className="cce-placeholders">
          <span className="cce-placeholders-label">
            <PlusCircle size={14} /> Insert field:
          </span>
          {placeholders.map((p) => (
            <button
              type="button"
              key={p}
              className="cce-placeholder-chip"
              onClick={() => insertPlaceholder(p)}
              disabled={disabled}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      <div className={`cce-body${showPreview ? ' with-preview' : ''}`}>
        <div className="cce-editor-pane">
          {mode === 'visual' ? (
            <>
              <div className="cce-toolbar">
                <ToolbarButton title="Bold" active={editor?.isActive('bold')} disabled={disabled} onClick={() => editor.chain().focus().toggleBold().run()}>
                  <TextB size={16} weight="bold" />
                </ToolbarButton>
                <ToolbarButton title="Italic" active={editor?.isActive('italic')} disabled={disabled} onClick={() => editor.chain().focus().toggleItalic().run()}>
                  <TextItalic size={16} />
                </ToolbarButton>
                <ToolbarButton title="Underline" active={editor?.isActive('underline')} disabled={disabled} onClick={() => editor.chain().focus().toggleUnderline().run()}>
                  <TextUnderline size={16} />
                </ToolbarButton>
                <ToolbarButton title="Strikethrough" active={editor?.isActive('strike')} disabled={disabled} onClick={() => editor.chain().focus().toggleStrike().run()}>
                  <TextStrikethrough size={16} />
                </ToolbarButton>
                <span className="cce-toolbar-sep" />
                <ToolbarButton title="Heading 1" active={editor?.isActive('heading', { level: 1 })} disabled={disabled} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>H1</ToolbarButton>
                <ToolbarButton title="Heading 2" active={editor?.isActive('heading', { level: 2 })} disabled={disabled} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</ToolbarButton>
                <ToolbarButton title="Heading 3" active={editor?.isActive('heading', { level: 3 })} disabled={disabled} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>H3</ToolbarButton>
                <ToolbarButton title="Paragraph" active={editor?.isActive('paragraph')} disabled={disabled} onClick={() => editor.chain().focus().setParagraph().run()}>P</ToolbarButton>
                <span className="cce-toolbar-sep" />
                <ToolbarButton title="Align left" active={editor?.isActive({ textAlign: 'left' })} disabled={disabled} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
                  <TextAlignLeft size={16} />
                </ToolbarButton>
                <ToolbarButton title="Align center" active={editor?.isActive({ textAlign: 'center' })} disabled={disabled} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
                  <TextAlignCenter size={16} />
                </ToolbarButton>
                <ToolbarButton title="Align right" active={editor?.isActive({ textAlign: 'right' })} disabled={disabled} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
                  <TextAlignRight size={16} />
                </ToolbarButton>
                <span className="cce-toolbar-sep" />
                <ToolbarButton title="Bullet list" active={editor?.isActive('bulletList')} disabled={disabled} onClick={() => editor.chain().focus().toggleBulletList().run()}>
                  <ListBullets size={16} />
                </ToolbarButton>
                <ToolbarButton title="Numbered list" active={editor?.isActive('orderedList')} disabled={disabled} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                  <ListNumbers size={16} />
                </ToolbarButton>
                <ToolbarButton title="Quote" active={editor?.isActive('blockquote')} disabled={disabled} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
                  <Quotes size={16} />
                </ToolbarButton>
                <span className="cce-toolbar-sep" />
                <ToolbarButton title="Undo" disabled={disabled} onClick={() => editor.chain().focus().undo().run()}>
                  <ArrowUUpLeft size={16} />
                </ToolbarButton>
                <ToolbarButton title="Redo" disabled={disabled} onClick={() => editor.chain().focus().redo().run()}>
                  <ArrowUUpRight size={16} />
                </ToolbarButton>
              </div>
              <EditorContent editor={editor} className="cce-editor-content" />
            </>
          ) : (
            <textarea
              ref={textareaRef}
              className="cce-source-textarea"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              rows={16}
              placeholder="<html>...<body>{{ student_name }}...</body></html>"
              disabled={disabled}
              required
            />
          )}
        </div>

        {showPreview && (
          <div className="cce-preview-pane">
            <div className="cce-preview-label">Sample preview (approximate — placeholders substituted with sample data)</div>
            <iframe
              title="Certificate preview"
              className="cce-preview-frame"
              srcDoc={previewHtml}
              sandbox=""
            />
          </div>
        )}
      </div>

      <DialogModal
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        primaryAction={{ label: confirmDialog?.confirmLabel || 'Confirm', onClick: () => confirmDialog?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
}

export default CertificateContentEditor;
